/* eslint-disable no-param-reassign */
import { LocationInquiryApi } from './api'
import { ADVANCED_STORAGE_KEY, RADIUS_MIN_METERS } from './types'
import {
  buildCoordinate,
  createRow,
  formatCoordInput,
  isRowBlank,
  isRowComplete,
  isWgsSystem,
  validateCoordinateRows,
  validateFile,
  toNumber,
} from './utils/coordinates'
import { isGpsForbidden, parseInquiryError } from './utils/errors'
import { notify } from './utils/notify'

function readStoredAdvanced() {
  try {
    const raw = localStorage.getItem(ADVANCED_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

function writeStoredAdvanced(advanced) {
  try {
    localStorage.setItem(ADVANCED_STORAGE_KEY, JSON.stringify({
      sources: advanced.sources,
      layers: advanced.layers,
      radius_meters: advanced.radius_meters,
      include_nearby: advanced.include_nearby,
      includeNearbyManual: advanced.includeNearbyManual,
      per_layer_limit: advanced.per_layer_limit,
      year: advanced.year,
    }))
  } catch (error) {
    // Storage can be unavailable. The inquiry still runs.
  }
}

function pickValue(options, preferred) {
  const values = (options || []).map(item => item.value)
  if (values.includes(preferred)) return preferred
  return values[0] || preferred
}

function clampAdvanced(advanced, options) {
  const limits = options?.limits || {}
  const allowedSources = (options?.sources || []).map(item => item.value)
  let sources = (advanced.sources || []).filter(value => allowedSources.includes(value))
  if (!sources.length) sources = allowedSources.slice()
  const knownLayers = new Set((options?.layers || []).map(item => item.key))
  const layers = (advanced.layers || []).filter(key => knownLayers.has(key))
  const maxRadius = Number(limits.max_radius_meters)
  let radius = Number(advanced.radius_meters)
  if (!Number.isFinite(radius)) radius = Number(limits.default_radius_meters)
  if (!Number.isFinite(radius)) radius = RADIUS_MIN_METERS
  const radiusCap = Number.isFinite(maxRadius) ? maxRadius : radius
  radius = Math.min(Math.max(radius, RADIUS_MIN_METERS), radiusCap)
  const maxPer = Number(limits.max_per_layer_limit)
  let perLayer = Number(advanced.per_layer_limit)
  if (!Number.isFinite(perLayer)) perLayer = Number(limits.default_per_layer_limit)
  if (!Number.isFinite(perLayer)) perLayer = 1
  const perCap = Number.isFinite(maxPer) ? maxPer : perLayer
  perLayer = Math.min(Math.max(Math.round(perLayer), 1), perCap)
  let { year } = advanced
  if (year === '' || year === undefined) year = null
  if (year !== null && year !== undefined) {
    const numericYear = Number(year)
    year = Number.isFinite(numericYear) ? numericYear : null
  }
  return {
    sources,
    layers,
    radius_meters: radius,
    include_nearby: Boolean(advanced.include_nearby),
    includeNearbyManual: Boolean(advanced.includeNearbyManual),
    per_layer_limit: perLayer,
    year,
  }
}

export function effectiveIncludeNearby(state) {
  if (state.mode === 'polygon') return false
  if (state.advanced.includeNearbyManual) return Boolean(state.advanced.include_nearby)
  if (state.inputType === 'current_location') return true
  if (state.inputType === 'file') return false
  const count = (state.rows || []).filter(row => isRowComplete(row, state.sourceSystem)).length
  return count <= 1
}

function buildInquiryBody(state) {
  const { advanced } = state
  const body = {
    input_type: state.inputType,
    mode: state.mode,
    sources: advanced.sources.slice(),
    per_layer_limit: advanced.per_layer_limit,
  }
  if (advanced.layers.length) body.layers = advanced.layers.slice()
  if (advanced.year !== null && advanced.year !== undefined && advanced.year !== '') {
    body.year = Number(advanced.year)
  }
  if (state.mode === 'points') {
    body.radius_meters = Number(advanced.radius_meters)
    body.include_nearby = effectiveIncludeNearby(state)
  }
  if (state.inputType === 'current_location') {
    body.currentLocation = {
      latitude: Number(state.gps.latitude),
      longitude: Number(state.gps.longitude),
    }
    return body
  }
  if (state.inputType === 'file') {
    body.file = state.file
    body.source_system = state.sourceSystem
    if ((state.sourceSystem === 'utm' || state.sourceSystem === 'auto') && state.utmZone) {
      body.utm_zone = state.utmZone
    }
    return body
  }
  body.source_system = state.sourceSystem
  if (state.sourceSystem === 'utm' || state.sourceSystem === 'auto') {
    body.utm_zone = state.utmZone || '36N'
  }
  body.coordinates = state.rows
    .filter(row => isRowComplete(row, state.sourceSystem))
    .map(row => buildCoordinate(row, state.sourceSystem, state.utmZone || '36N'))
  return body
}

function clientFieldErrors(state) {
  const fieldErrors = {}
  if (!state.advanced.sources.length) {
    fieldErrors.sources = ['اختر مصدراً واحداً على الأقل']
  }
  if (state.inputType === 'current_location') {
    if (state.mode === 'polygon') fieldErrors.mode = ['موقعي الحالي متاح في وضع النقاط فقط']
    if (state.gps.status !== 'ready') {
      fieldErrors.currentLocation = ['فعّل خدمة الموقع (GPS) للاستعلام بموقعك الحالي']
    }
  } else if (state.inputType === 'file') {
    const fileError = validateFile(state.file, state.options?.file_extensions, state.options?.limits?.max_file_kb)
    if (fileError) fieldErrors.file = [fileError]
  } else {
    const check = validateCoordinateRows(state.rows, state.sourceSystem, state.mode, state.options?.limits)
    if (check.errors.length) fieldErrors.coordinates = check.errors
  }
  return fieldErrors
}

function readGps() {
  return new Promise(resolve => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({
        status: 'unavailable', latitude: null, longitude: null, accuracy: null,
      })
      return
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          status: 'ready',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        })
      },
      error => {
        resolve({
          status: error && error.code === 1 ? 'denied' : 'unavailable',
          latitude: null,
          longitude: null,
          accuracy: null,
        })
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    )
  })
}

function clearOutcome(state) {
  state.result = null
  state.selectedIndex = null
  state.selectedFeature = null
  state.fieldErrors = {}
  state.formMessage = ''
  state.standaloneFailed = null
}

const locationInquiryModule = {
  namespaced: true,
  state: {
    options: null,
    optionsLoading: false,
    optionsError: '',
    mode: 'points',
    inputType: 'current_location',
    sourceSystem: 'auto',
    utmZone: '36N',
    rows: [createRow()],
    file: null,
    fileError: '',
    gps: {
      status: 'idle',
      latitude: null,
      longitude: null,
      accuracy: null,
    },
    pickOnMap: false,
    advanced: {
      sources: [],
      layers: [],
      radius_meters: null,
      include_nearby: true,
      includeNearbyManual: false,
      per_layer_limit: null,
      year: null,
    },
    loading: false,
    uploadProgress: 0,
    fieldErrors: {},
    formMessage: '',
    result: null,
    selectedIndex: null,
    selectedFeature: null,
    failedOpen: false,
    standaloneFailed: null,
  },
  getters: {
    limits: state => state.options?.limits || {},
    effectiveIncludeNearby: state => effectiveIncludeNearby(state),
    selectedPoint(state) {
      if (!state.result || state.result.mode !== 'points') return null
      if (state.selectedIndex === null || state.selectedIndex === undefined) return null
      return (state.result.results || []).find(item => item.index === state.selectedIndex) || null
    },
    draftPoints(state) {
      const showDraft = !state.result || state.pickOnMap
      if (!showDraft) return []
      if (state.inputType === 'current_location' && state.gps.status === 'ready') {
        return [{ latitude: Number(state.gps.latitude), longitude: Number(state.gps.longitude) }]
      }
      if (state.inputType !== 'coordinates') return []
      return state.rows.reduce((list, row) => {
        const latitude = toNumber(row.latitude)
        const longitude = toNumber(row.longitude)
        if (latitude === null || longitude === null) return list
        list.push({ latitude, longitude })
        return list
      }, [])
    },
    rowLimit(state) {
      const limits = state.options?.limits || {}
      if (state.mode === 'polygon') return limits.max_polygon_vertices || 0
      return limits.max_points_per_request || 0
    },
  },
  mutations: {
    SET_OPTIONS_LOADING(state, value) {
      state.optionsLoading = value
    },
    SET_OPTIONS_ERROR(state, value) {
      state.optionsError = value || ''
    },
    APPLY_OPTIONS(state, options) {
      state.options = options
      state.mode = pickValue(options.modes, 'points')
      state.inputType = pickValue(options.input_types, 'current_location')
      state.sourceSystem = pickValue(options.source_systems, 'auto')
      const stored = readStoredAdvanced()
      const seed = stored || {
        sources: (options.sources || []).map(item => item.value),
        layers: [],
        radius_meters: options.limits?.default_radius_meters,
        include_nearby: true,
        includeNearbyManual: false,
        per_layer_limit: options.limits?.default_per_layer_limit,
        year: null,
      }
      if (stored && stored.include_nearby !== undefined) seed.includeNearbyManual = true
      state.advanced = clampAdvanced(seed, options)
      if (!state.rows.length) state.rows = [createRow()]
    },
    SET_MODE(state, value) {
      if (state.mode === value) return
      state.mode = value
      clearOutcome(state)
      if (value === 'polygon' && state.inputType === 'current_location') {
        const types = (state.options?.input_types || []).map(item => item.value)
        if (types.includes('coordinates')) state.inputType = 'coordinates'
        else if (types.includes('file')) state.inputType = 'file'
      }
    },
    SET_INPUT_TYPE(state, value) {
      if (state.mode === 'polygon' && value === 'current_location') return
      if (state.inputType === value) return
      state.inputType = value
      clearOutcome(state)
    },
    SET_SOURCE_SYSTEM(state, value) {
      state.sourceSystem = value
      state.fieldErrors = {}
    },
    SET_UTM_ZONE(state, value) {
      state.utmZone = value
    },
    SET_ROWS(state, rows) {
      state.rows = rows && rows.length ? rows : [createRow()]
      state.fieldErrors = {}
    },
    UPDATE_ROW(state, { index, field, value }) {
      const current = state.rows[index]
      if (!current) return
      const next = state.rows.slice()
      next.splice(index, 1, { ...current, [field]: value })
      state.rows = next
      state.fieldErrors = {}
    },
    ADD_ROW(state) {
      state.rows = state.rows.concat([createRow()])
    },
    REMOVE_ROW(state, index) {
      const next = state.rows.slice()
      next.splice(index, 1)
      state.rows = next.length ? next : [createRow()]
      state.fieldErrors = {}
    },
    MOVE_ROW(state, { index, direction }) {
      const target = index + direction
      if (target < 0 || target >= state.rows.length) return
      const next = state.rows.slice()
      const [item] = next.splice(index, 1)
      next.splice(target, 0, item)
      state.rows = next
    },
    ADD_WGS84_POINT(state, point) {
      const row = createRow({
        latitude: formatCoordInput(point.latitude),
        longitude: formatCoordInput(point.longitude),
      })
      const next = state.rows.slice()
      const last = next[next.length - 1]
      if (last && isRowBlank(last, state.sourceSystem)) next.splice(next.length - 1, 1, row)
      else next.push(row)
      state.rows = next
      state.fieldErrors = {}
    },
    SET_FILE(state, file) {
      state.file = file
      state.fileError = ''
      state.fieldErrors = {}
    },
    SET_FILE_ERROR(state, message) {
      state.fileError = message || ''
    },
    SET_GPS(state, gps) {
      state.gps = {
        status: gps.status || 'idle',
        latitude: gps.latitude ?? null,
        longitude: gps.longitude ?? null,
        accuracy: gps.accuracy ?? null,
      }
    },
    SET_PICK(state, value) {
      state.pickOnMap = Boolean(value)
    },
    PATCH_ADVANCED(state, patch) {
      state.advanced = clampAdvanced({ ...state.advanced, ...patch }, state.options || {})
      writeStoredAdvanced(state.advanced)
    },
    TOGGLE_SOURCE(state, value) {
      const sources = state.advanced.sources.slice()
      const index = sources.indexOf(value)
      if (index >= 0) {
        if (sources.length === 1) return
        sources.splice(index, 1)
      } else {
        sources.push(value)
      }
      state.advanced = { ...state.advanced, sources }
      writeStoredAdvanced(state.advanced)
    },
    TOGGLE_LAYER(state, key) {
      const layers = state.advanced.layers.slice()
      const index = layers.indexOf(key)
      if (index >= 0) layers.splice(index, 1)
      else layers.push(key)
      state.advanced = { ...state.advanced, layers }
      writeStoredAdvanced(state.advanced)
    },
    SET_LOADING(state, value) {
      state.loading = value
    },
    SET_PROGRESS(state, value) {
      state.uploadProgress = value
    },
    SET_FIELD_ERRORS(state, fieldErrors) {
      state.fieldErrors = fieldErrors || {}
    },
    SET_FORM_MESSAGE(state, message) {
      state.formMessage = message || ''
    },
    SET_RESULT(state, result) {
      state.result = result
    },
    SET_SELECTED_INDEX(state, index) {
      state.selectedIndex = index
      state.selectedFeature = null
    },
    SET_SELECTED_FEATURE(state, feature) {
      state.selectedFeature = feature
    },
    SET_FAILED_OPEN(state, value) {
      state.failedOpen = Boolean(value)
    },
    SET_STANDALONE_FAILED(state, value) {
      state.standaloneFailed = value
    },
  },
  actions: {
    async loadOptions({ state, commit }) {
      if (state.options) return
      commit('SET_OPTIONS_LOADING', true)
      commit('SET_OPTIONS_ERROR', '')
      try {
        const options = await LocationInquiryApi.options()
        if (!options || !options.input_types) {
          throw new Error('تعذر قراءة خيارات الاستعلام')
        }
        commit('APPLY_OPTIONS', options)
      } catch (error) {
        const parsed = await parseInquiryError(error)
        const message = parsed.status === 403
          ? 'ليست لديك صلاحية الاستعلام بالموقع'
          : (parsed.message || 'تعذر تحميل خيارات الاستعلام')
        commit('SET_OPTIONS_ERROR', message)
      } finally {
        commit('SET_OPTIONS_LOADING', false)
      }
    },
    refreshGps({ commit }) {
      commit('SET_GPS', { status: 'loading' })
      return readGps().then(gps => {
        commit('SET_GPS', gps)
        return gps
      })
    },
    addMapPoint({ state, commit }, point) {
      const limit = state.mode === 'polygon'
        ? state.options?.limits?.max_polygon_vertices
        : state.options?.limits?.max_points_per_request
      const filled = state.rows.filter(row => !isRowBlank(row, isWgsSystem(state.sourceSystem) ? state.sourceSystem : 'wgs84')).length
      if (limit && filled >= limit) {
        notify(state.mode === 'polygon'
          ? `الحد الأقصى لرؤوس القطعة هو ${limit}`
          : `الحد الأقصى لعدد النقاط هو ${limit}`, 'warning', 'AlertTriangleIcon')
        return
      }
      commit('ADD_WGS84_POINT', point)
    },
    acceptFile({ state, commit }, file) {
      const message = validateFile(file, state.options?.file_extensions, state.options?.limits?.max_file_kb)
      if (message) {
        commit('SET_FILE_ERROR', message)
        return
      }
      commit('SET_FILE', file)
    },
    async downloadTemplate(context, system) {
      try {
        const response = await LocationInquiryApi.template(system)
        const blob = response?.data
        if (!blob) {
          notify('تعذر تحميل النموذج', 'danger')
          return
        }
        const signature = new Uint8Array(await blob.slice(0, 2).arrayBuffer())
        const isZip = signature[0] === 0x50 && signature[1] === 0x4b
        if (!isZip) {
          let message = 'تعذر تحميل النموذج'
          try {
            const parsed = JSON.parse(await blob.text())
            message = parsed.message || message
          } catch (error) {
            message = 'تعذر تحميل النموذج'
          }
          notify(message, 'danger')
          return
        }
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `location_inquiry_template_${system}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (error) {
        const parsed = await parseInquiryError(error)
        if (parsed.status === 401) return
        if (parsed.status === 403) {
          notify('ليست لديك صلاحية الاستعلام بالموقع', 'danger', 'LockIcon')
          return
        }
        notify(parsed.message || 'تعذر تحميل النموذج', 'danger')
      }
    },
    selectFeature({ state, commit }, feature) {
      if (!feature || !Array.isArray(feature.bbox)) {
        commit('SET_SELECTED_FEATURE', null)
        return
      }
      const key = `${feature.layer_id || feature.layer?.id || 0}:${feature.id}`
      if (state.selectedFeature && state.selectedFeature.key === key) {
        commit('SET_SELECTED_FEATURE', null)
        return
      }
      commit('SET_SELECTED_FEATURE', {
        key,
        bbox: feature.bbox,
        centroid: feature.centroid,
      })
    },
    async submit({ state, commit, dispatch }) {
      if (state.loading) return
      commit('SET_FIELD_ERRORS', {})
      commit('SET_FORM_MESSAGE', '')
      if (state.inputType === 'current_location') {
        await dispatch('refreshGps')
      }
      const fieldErrors = clientFieldErrors(state)
      if (Object.keys(fieldErrors).length) {
        commit('SET_FIELD_ERRORS', fieldErrors)
        return
      }
      commit('SET_LOADING', true)
      commit('SET_PROGRESS', 0)
      try {
        const result = await LocationInquiryApi.inquire(buildInquiryBody(state), event => {
          if (!event || !event.total) return
          commit('SET_PROGRESS', Math.round((event.loaded / event.total) * 100))
        })
        commit('SET_RESULT', result)
        commit('SET_SELECTED_INDEX', null)
        commit('SET_SELECTED_FEATURE', null)
        commit('SET_STANDALONE_FAILED', null)
        commit('SET_FIELD_ERRORS', {})
        commit('SET_FORM_MESSAGE', '')
      } catch (error) {
        const parsed = await parseInquiryError(error)
        if (parsed.status === 401) return
        if (parsed.status === 403) {
          if (isGpsForbidden(parsed)) {
            const text = 'فعّل خدمة الموقع (GPS) للاستعلام بموقعك الحالي'
            commit('SET_FORM_MESSAGE', text)
            commit('SET_FIELD_ERRORS', { currentLocation: [text] })
          } else {
            commit('SET_FORM_MESSAGE', 'ليست لديك صلاحية الاستعلام بالموقع')
          }
          return
        }
        if (parsed.kind === 'failed') {
          commit('SET_FORM_MESSAGE', parsed.message || 'تعذر قراءة الإحداثيات')
          commit('SET_STANDALONE_FAILED', {
            message: parsed.message || '',
            failed: parsed.failed,
            source: state.inputType,
          })
          commit('SET_FAILED_OPEN', true)
          return
        }
        if (parsed.kind === 'fields') {
          commit('SET_FIELD_ERRORS', parsed.fieldErrors)
          if (parsed.message) commit('SET_FORM_MESSAGE', parsed.message)
          return
        }
        const message = parsed.message || 'تعذر تنفيذ الاستعلام'
        commit('SET_FORM_MESSAGE', message)
        notify(message, 'danger')
      } finally {
        commit('SET_LOADING', false)
        commit('SET_PROGRESS', 0)
      }
    },
  },
}

export default locationInquiryModule
